/**
 * "Edit Existing Data" — the rail's third button (client, 2026-09-18).
 *
 * Pick a dataset, then edit its rows in place and publish the result as a new version. Two
 * steps rather than one screen with a select in the corner: choosing *which* dataset is the
 * decision that cannot be undone by scrolling, and a grid that silently swapped its rows
 * under an unsaved edit would lose work.
 *
 * Rasters are listed and refused here — a pixel grid has no rows — and the refusal carries
 * its reason on the row itself rather than in a toast after the click.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import type { ManagedDatasetRow } from '../../lib/api';
import { DatasetPicker } from './DatasetPicker';
import { RowsGrid } from './RowsGrid';

export function EditDataPanel() {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<ManagedDatasetRow | null>(null);

  return (
    <section className="card mgr-panelcard" data-testid="mgr-edit-data">
      <div className="card-head">
        <div className="card-head-text">
          <h3>{t('manager.editData.title')}</h3>
          <p className="sub">{t('manager.editData.sub')}</p>
        </div>
        {picked ? (
          <Btn size="sm" icon="chev" data-testid="mgr-edit-data-change" onClick={() => setPicked(null)}>
            {t('manager.editData.change')}
          </Btn>
        ) : null}
      </div>

      {picked === null ? (
        <DatasetPicker
          value={null}
          onPick={setPicked}
          label={t('manager.editData.which')}
          hint={t('manager.editData.whichHint')}
          testId="mgr-edit-ds"
          // Rasters are not listed at all (client, 2026-09-21): nobody edits pixels row by row.
          include={(row) => row.data_type !== 'raster'}
          // A vector or table whose rows the catalogue does not carry (`has_rows`) has
          // nothing for the grid to open — seeded and provider-backed rows included.
          disabledReason={(row) => (row.has_rows === false ? t('manager.editData.noRows') : null)}
        />
      ) : (
        <>
          <p className="mgr-panel-target" data-testid="mgr-edit-data-target">
            <strong>{picked.title}</strong> <code>{picked.id}</code>
          </p>
          <RowsGrid
            key={picked.id}
            datasetId={picked.id}
            dataType={picked.data_type}
            title={picked.title}
          />
        </>
      )}
    </section>
  );
}

export default EditDataPanel;
